// 🎨 Générateur de visuels réseaux sociaux du concours.
// Un seul SVG par contenu, utilisé À LA FOIS comme aperçu (dans un <img>) et
// comme fichier téléchargé (PNG via canvas) → ce que le user voit = ce qu'il obtient.
// Aucune dépendance externe, aucune image distante non maîtrisée (photo/logo passés
// en data-URI par l'appelant, sinon dégradé de marque).

const esc = (s = '') => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Dégradés par ambiance (mêmes couleurs que la maquette validée)
const GRADS = {
  launch: ['#5A35FF', '#7C3AED', '#2563EB'],
  amber:  ['#F59E0B', '#EA580C', '#B91C1C'],
  red:    ['#EF4444', '#B91C1C', '#450A0A'],
  gold:   ['#FBBF24', '#F5A623', '#B45309'],
  warm:   ['#A855F7', '#EC4899', '#F59E0B'],
  dark:   ['#312852', '#221C40', '#1A1535'],
  photo:  ['#7A5A3F', '#5A4230', '#3A2A1C'],
};

// Formats natifs (largeur × hauteur en px)
const SIZES = {
  post:   [1080, 1080],
  story:  [1080, 1920],
  reel:   [1080, 1920],
  poster: [1080, 1350],
};

// Badges plateformes — petit glyphe blanc sur pastille
function badgeSvg(kind, x, y, s = 58) {
  const fills = {
    ig: 'url(#gIg)', fb: '#1877F2', reel: 'url(#gReel)', qr: '#1A1535',
  };
  const glyph = {
    ig: `<rect x="${x + 14}" y="${y + 14}" width="${s - 28}" height="${s - 28}" rx="9" fill="none" stroke="#fff" stroke-width="4"/><circle cx="${x + s / 2}" cy="${y + s / 2}" r="9" fill="none" stroke="#fff" stroke-width="4"/><circle cx="${x + s - 17}" cy="${y + 17}" r="3.2" fill="#fff"/>`,
    fb: `<path transform="translate(${x + 15},${y + 13}) scale(1.2)" fill="#fff" d="M14 8.5V7c0-.7.3-1 1-1h1.5V3H14c-2.2 0-3.5 1.3-3.5 3.7V8.5H8.5V12h2V21H14v-9h2.3l.5-3.5H14z"/>`,
    reel: `<rect x="${x + 14}" y="${y + 16}" width="${s - 28}" height="${s - 32}" rx="7" fill="none" stroke="#fff" stroke-width="4"/><path d="M${x + 25} ${y + 22} L${x + 38} ${y + 29} L${x + 25} ${y + 36} Z" fill="#fff"/>`,
    qr: `<g fill="#fff"><rect x="${x + 15}" y="${y + 15}" width="10" height="10"/><rect x="${x + 33}" y="${y + 15}" width="10" height="10"/><rect x="${x + 15}" y="${y + 33}" width="10" height="10"/><rect x="${x + 33}" y="${y + 33}" width="5" height="5"/></g>`,
  };
  return `<rect x="${x}" y="${y}" width="${s}" height="${s}" rx="16" fill="${fills[kind] || '#333'}"/>${glyph[kind] || ''}`;
}

/**
 * Construit le SVG d'un visuel.
 * @param {object} v item du plan (format, bg, kicker, titleLines, heroEmoji, pill, mode, badges, reelTag, duration, subtitle)
 * @param {object} ctx { company, logoDataUri, photoDataUri, accent }
 * @returns {string} SVG
 */
export function buildVisualSVG(v, ctx = {}) {
  const [W, H] = SIZES[v.format] || SIZES.post;
  const company = esc(ctx.company || '');
  const g = GRADS[v.bg] || GRADS.launch;
  const isDarkText = v.mode === 'poster';
  const ink = '#1A1535';
  const usePhoto = (v.mode === 'photo' || v.mode === 'reel') && ctx.photoDataUri;

  const cx = W / 2;
  const pad = 64;

  // ── defs (dégradés) ──
  const defs = `<defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="${g[0]}"/><stop offset="0.55" stop-color="${g[1]}"/><stop offset="1" stop-color="${g[2]}"/>
    </linearGradient>
    <radialGradient id="glow" cx="0.8" cy="0.12" r="0.7"><stop offset="0" stop-color="#ffffff" stop-opacity="0.24"/><stop offset="0.6" stop-color="#ffffff" stop-opacity="0"/></radialGradient>
    <linearGradient id="scrim" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#000" stop-opacity="0.42"/><stop offset="0.34" stop-color="#000" stop-opacity="0"/><stop offset="0.55" stop-color="#000" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity="0.8"/></linearGradient>
    <linearGradient id="gIg" x1="0" y1="1" x2="1" y2="0"><stop offset="0" stop-color="#FEDA75"/><stop offset="0.35" stop-color="#FA7E1E"/><stop offset="0.6" stop-color="#D62976"/><stop offset="0.8" stop-color="#962FBF"/><stop offset="1" stop-color="#4F5BD5"/></linearGradient>
    <linearGradient id="gReel" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#833AB4"/><stop offset="0.5" stop-color="#FD1D1D"/><stop offset="1" stop-color="#FCB045"/></linearGradient>
  </defs>`;

  // ── fond ──
  let bg;
  if (v.mode === 'poster') {
    bg = `<rect width="${W}" height="${H}" fill="#ffffff"/>`;
  } else if (usePhoto) {
    bg = `<image href="${ctx.photoDataUri}" x="0" y="0" width="${W}" height="${H}" preserveAspectRatio="xMidYMid slice"/><rect width="${W}" height="${H}" fill="url(#scrim)"/>`;
  } else {
    bg = `<rect width="${W}" height="${H}" fill="url(#bg)"/><rect width="${W}" height="${H}" fill="url(#glow)"/>`;
  }

  // ── marque (haut gauche) ──
  const logoBox = ctx.logoDataUri
    ? `<image href="${ctx.logoDataUri}" x="${pad}" y="${pad - 6}" width="52" height="52" preserveAspectRatio="xMidYMid meet"/>`
    : `<rect x="${pad}" y="${pad - 6}" width="52" height="52" rx="14" fill="${isDarkText ? '#EDE9FF' : 'rgba(255,255,255,0.24)'}"/><text x="${pad + 26}" y="${pad + 28}" font-size="26" text-anchor="middle" dominant-baseline="middle">🔧</text>`;
  const brand = `${logoBox}<text x="${pad + 66}" y="${pad + 24}" font-size="26" font-weight="800" letter-spacing="1.5" fill="${isDarkText ? ink : '#fff'}">${company.toUpperCase()}</text>`;

  // ── badges (haut droite) ──
  let bx = W - pad - 58;
  let badges = '';
  (v.badges || []).slice().reverse().forEach(k => { badges += badgeSvg(k, bx, pad - 6); bx -= 68; });

  // ── contenu : bloc de texte empilé proprement (aucun chevauchement) ──
  const white = '#fff';
  const centered = v.align === 'center';
  const tx = centered ? cx : pad;
  const anchor = centered ? 'middle' : 'start';

  const titleSz = v.titleSize || 72;
  const rowKicker = v.kicker ? 52 : 0;
  const rowTitle = titleSz * 1.18;
  const rowSub = v.subtitle ? 54 : 0;
  const rowPill = v.pill ? 84 : 0;
  const blockH = rowKicker + (v.titleLines || []).length * rowTitle + rowSub + rowPill;
  const heroH = v.heroEmoji ? 168 : 0;

  // Empile le bloc à partir de startTop ; y avance en ligne de base
  const renderBlock = (startTop) => {
    let y = startTop, out = '';
    if (v.kicker) {
      y += 34;
      out += `<text x="${tx}" y="${y}" font-size="28" font-weight="800" letter-spacing="3" text-anchor="${anchor}" fill="${v.kickerColor || (isDarkText ? '#E8920C' : 'rgba(255,255,255,0.92)')}">${esc(v.kicker).toUpperCase()}</text>`;
      y += rowKicker - 34;
    }
    (v.titleLines || []).forEach(line => {
      y += titleSz * 0.82;
      out += `<text x="${tx}" y="${y}" font-size="${titleSz}" font-weight="900" letter-spacing="-1.5" text-anchor="${anchor}" fill="${isDarkText ? ink : white}">${esc(line)}</text>`;
      y += rowTitle - titleSz * 0.82;
    });
    if (v.subtitle) {
      y += 34;
      out += `<text x="${tx}" y="${y}" font-size="30" font-weight="700" text-anchor="${anchor}" fill="${isDarkText ? '#6B7280' : 'rgba(255,255,255,0.94)'}">${esc(v.subtitle)}</text>`;
      y += rowSub - 34;
    }
    if (v.pill) {
      y += 12;
      const pw = 52 + v.pill.length * 17;
      const px = centered ? cx - pw / 2 : tx;
      const pfill = v.pillGhost ? 'rgba(255,255,255,0.18)' : '#fff';
      const pstroke = v.pillGhost ? 'stroke="rgba(255,255,255,0.65)" stroke-width="3"' : '';
      const pcol = v.pillGhost ? '#fff' : ink;
      out += `<rect x="${px}" y="${y}" width="${pw}" height="60" rx="30" fill="${pfill}" ${pstroke}/><text x="${px + pw / 2}" y="${y + 40}" font-size="27" font-weight="900" text-anchor="middle" fill="${pcol}">${esc(v.pill)}</text>`;
    }
    return out;
  };

  let content = '';

  if (v.mode === 'poster') {
    // Affiche : titre EN HAUT, QR au centre, sous-titre SOUS le QR — jamais superposés
    const qs = 320, qy = H * 0.40;
    if (v.kicker) content += `<text x="${cx}" y="${qy - 44}" font-size="40" font-weight="900" letter-spacing="1" text-anchor="middle" fill="${ink}">${esc(v.kicker).toUpperCase()}</text>`;
    content += `<rect x="${cx - qs / 2}" y="${qy}" width="${qs}" height="${qs}" rx="26" fill="#fff" stroke="${ink}" stroke-width="6"/>`;
    content += qrPlaceholder(cx - qs / 2 + 26, qy + 26, qs - 52, ink);
    if (v.subtitle) content += `<text x="${cx}" y="${qy + qs + 60}" font-size="30" font-weight="700" text-anchor="middle" fill="#6B7280">${esc(v.subtitle)}</text>`;
  } else {
    // Reel : play au centre haut, tag + durée
    if (v.mode === 'reel') {
      content += `<circle cx="${cx}" cy="${H * 0.42}" r="66" fill="rgba(255,255,255,0.92)"/><path d="M${cx - 20} ${H * 0.42 - 30} L${cx + 34} ${H * 0.42} L${cx - 20} ${H * 0.42 + 30} Z" fill="${g[2]}"/>`;
      if (v.reelTag) content += `<rect x="${pad}" y="${pad + 74}" width="${44 + v.reelTag.length * 15}" height="46" rx="10" fill="rgba(0,0,0,0.5)"/><text x="${pad + 20}" y="${pad + 104}" font-size="24" font-weight="800" letter-spacing="2" fill="#fff">${esc(v.reelTag)}</text>`;
      if (v.duration) content += `<rect x="${W - pad - 96}" y="${H - pad - 46}" width="96" height="46" rx="10" fill="rgba(0,0,0,0.6)"/><text x="${W - pad - 48}" y="${H - pad - 15}" font-size="26" font-weight="800" fill="#fff" text-anchor="middle">${esc(v.duration)}</text>`;
    }

    // Position du bloc texte
    let blockTop;
    if (v.mode === 'reel' || !centered) blockTop = H - pad - blockH;   // ancré en bas
    else blockTop = H * 0.46 - (heroH + blockH) / 2 + heroH;           // story centrée

    // Hero emoji juste au-dessus du bloc
    if (v.heroEmoji) {
      const hx = centered ? cx : tx;
      content += `<text x="${hx}" y="${blockTop - 22}" font-size="132" text-anchor="${centered ? 'middle' : 'start'}">${v.heroEmoji}</text>`;
    }
    content += renderBlock(blockTop);
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" font-family="-apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif">${defs}${bg}${brand}${badges}${content}</svg>`;
}

function qrPlaceholder(x, y, size, color) {
  const cells = [[0, 0, 3, 3], [5, 0, 3, 3], [0, 5, 3, 3], [3.5, 0.8, 0.5, 0.5], [4.6, 0.8, 0.5, 0.5], [5, 3.6, 0.5, 0.5], [3.6, 4, 0.5, 0.5], [6, 4.5, 0.5, 0.5], [4.3, 6.2, 0.5, 0.5], [6, 6.8, 0.5, 0.5]];
  const u = size / 8;
  let out = '';
  cells.forEach(([cx, cy, w, hh]) => { out += `<rect x="${x + cx * u}" y="${y + cy * u}" width="${w * u}" height="${hh * u}" fill="${color}"/>`; });
  // évider le centre des 3 carrés de repère
  [[0, 0], [5, 0], [0, 5]].forEach(([px, py]) => {
    out += `<rect x="${x + (px + 0.6) * u}" y="${y + (py + 0.6) * u}" width="${1.8 * u}" height="${1.8 * u}" fill="#fff"/><rect x="${x + (px + 1.1) * u}" y="${y + (py + 1.1) * u}" width="${0.8 * u}" height="${0.8 * u}" fill="${color}"/>`;
  });
  return out;
}

// Convertit un SVG string en data-URI utilisable dans <img src>
export function svgToDataUri(svg) {
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}

// Télécharge le SVG en PNG à sa résolution native (via canvas).
export function downloadSvgAsPng(svg, filename, onError) {
  try {
    const m = svg.match(/width="(\d+)" height="(\d+)"/);
    const W = m ? +m[1] : 1080, H = m ? +m[2] : 1080;
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = W; canvas.height = H;
        const c = canvas.getContext('2d');
        c.fillStyle = '#ffffff'; c.fillRect(0, 0, W, H);
        c.drawImage(img, 0, 0, W, H);
        canvas.toBlob(blob => {
          if (!blob) { onError && onError(); return; }
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = filename;
          a.click();
          setTimeout(() => URL.revokeObjectURL(a.href), 1000);
        }, 'image/png');
      } catch (e) { onError && onError(e); }
    };
    img.onerror = () => onError && onError();
    img.src = svgToDataUri(svg);
  } catch (e) { onError && onError(e); }
}

/**
 * Plan de diffusion enrichi : 3 moments, 9 contenus, chacun avec son visuel + sa légende.
 */
export function buildSocialPlan({ prize, company, endDate }) {
  const p = (prize || 'le lot').replace(/^[^\p{L}\p{N}]+\s*/u, '');
  const pShort = shortWords(p, 34); // jamais coupé en plein milieu d'un mot
  const titleLines = splitTitle(p);
  return [
    {
      key: 'lancement', icon: '🚀', title: 'Lancement', when: 'Jour 1-2', dot: '#10B981',
      items: [
        {
          id: 'post-annonce', postWhen: { day: 'Jour 1', note: 'le grand lancement' }, format: 'post', bg: 'photo', mode: 'photo', badges: ['ig', 'fb'],
          kicker: '🎁 Grand jeu-concours', kickerColor: '#FFD469', titleLines, titleSize: 60, pill: 'Participe · lien en bio',
          doLabel: 'Post feed', doTag: { label: 'légende', kind: 'txt' },
          caption: `On offre ${lower(pShort)} à un·e client·e ce mois-ci 🎁\n\nPour tenter ta chance : lien en bio, 10 secondes chrono.\nTirage le ${endDate} — bonne chance à tou·te·s 🍀`,
          why: 'Le jour du lancement fait le pic d\'inscriptions. Un beau visuel + le lien = un maximum de participants dès le départ.',
        },
        {
          id: 'story-lancement', postWhen: { day: 'Jour 1', note: 'juste après le post' }, format: 'story', bg: 'launch', mode: 'gradient', align: 'center', badges: ['ig'],
          heroEmoji: '🎁', kicker: 'À gagner', titleLines: splitTitle(p, 20), titleSize: 56, pill: '👆 Appuie sur le lien', pillGhost: true,
          doLabel: 'Story · sticker Lien', doTag: { label: 'texte court', kind: 'txt' },
          caption: `${cap(pShort)} à gagner, ça te dit ? 👀 Appuie sur le lien 👆`,
          why: 'Les stories touchent tes abonnés les plus fidèles — ceux qui participent en premier et partagent à leurs amis.',
        },
        {
          id: 'affiche', postWhen: { day: 'Dès le Jour 1', note: 'en boutique, en continu' }, format: 'poster', bg: 'launch', mode: 'poster', align: 'center', badges: ['qr'],
          kicker: 'Scannez · participez · gagnez', titleLines: [], subtitle: '10 secondes chrono · gratuit',
          doLabel: '🖨 Affiche comptoir', doTag: { label: 'à imprimer', kind: 'txt' }, isPoster: true,
          caption: `Le QR se scanne en boutique — le seul endroit où il marche. À poser à la caisse ou en vitrine.`,
          why: '80% de tes clients passent en boutique, pas sur Instagram. L\'affiche capte ceux qui ne te suivent pas encore.',
        },
      ],
    },
    {
      key: 'pendant', icon: '🔥', title: 'Pendant', when: 'À mi-parcours', dot: '#F59E0B',
      items: [
        {
          id: 'story-countdown', postWhen: { day: 'Mi-parcours', note: '~ à la moitié du concours' }, format: 'story', bg: 'amber', mode: 'gradient', align: 'center', badges: ['ig'],
          kicker: '⏳ ça se termine', heroEmoji: '', titleLines: ['J-3'], titleSize: 168, subtitle: `${cap(pShort)} 🎁`, pill: 'Il est encore temps 👆', pillGhost: true,
          doLabel: 'Story compte à rebours', doTag: { label: 'texte court', kind: 'txt' },
          caption: `Plus que quelques jours ⏳ Le tirage approche — dernière chance de jouer 👆`,
          why: 'L\'urgence fait bouger les indécis. Le compte à rebours relance ceux qui ont vu la page sans s\'inscrire.',
        },
        {
          id: 'reel-coulisses', postWhen: { day: 'Mi-parcours', note: 'quand tu as 2 minutes' }, format: 'reel', bg: 'dark', mode: 'reel', badges: ['reel'], reelTag: '🎬 COULISSES', duration: '0:15',
          titleLines: ['Le lot en action'], titleSize: 46,
          doLabel: '🎬 Reel — à filmer', doTag: { label: 'vidéo · 15s', kind: 'vid' }, isVideo: true,
          caption: `🎬 À FILMER (15s) : montre le lot en situation (l'objet, le service en action, l'équipe qui prépare).\nTexte à l'écran : « À gagner jusqu'au ${endDate} 🎁 ». Musique tendance.`,
          why: 'Montrer le lot en vrai le rend désirable — et la vidéo est le format que l\'algorithme pousse le plus.',
        },
        {
          id: 'post-relance', postWhen: { day: 'Mi-parcours', note: 'un coup de relance' }, format: 'post', bg: 'launch', mode: 'proof', badges: ['fb'],
          heroEmoji: '🍀', titleLines: ['Ils sont déjà', 'nombreux à jouer'], titleSize: 58, subtitle: '…et toi, tu attends quoi ?', pill: 'Participe · lien en bio',
          doLabel: 'Post relance mi-parcours', doTag: { label: 'légende', kind: 'txt' },
          caption: `Ils sont déjà nombreux à avoir tenté leur chance 🍀 Et toi ?\n\n${cap(pShort)} à gagner — participation en 10 secondes.`,
          why: 'La preuve sociale (« ils sont déjà nombreux ») rassure et déclenche le réflexe « moi aussi je joue ».',
        },
      ],
    },
    {
      key: 'fin', icon: '🏁', title: 'Clôture', when: 'Derniers jours + après le tirage', dot: '#6C5CE7',
      items: [
        {
          id: 'post-derniere', postWhen: { day: 'La veille (J-1)', note: 'avant le tirage' }, format: 'post', bg: 'red', mode: 'urgent', badges: ['fb'],
          kicker: '⏰ Dernier jour', kickerColor: '#FEE2E2', titleLines: ['Le tirage,', "c'est demain"], titleSize: 68, pill: 'Vite · lien en bio',
          doLabel: 'Post dernière chance (J-1)', doTag: { label: 'légende', kind: 'txt' },
          caption: `DERNIER JOUR ⏰ Le tirage a lieu demain !\n\n${cap(pShort)} à gagner — c'est maintenant ou jamais.`,
          why: 'Le dernier jour concentre souvent près d\'un tiers des inscriptions — c\'est le post à ne surtout pas rater.',
        },
        {
          id: 'reel-gagnant', postWhen: { day: 'Jour du tirage', note: 'le grand moment 🥁' }, format: 'reel', bg: 'gold', mode: 'reel', align: 'center', badges: ['reel'], reelTag: '🥁 LE TIRAGE', duration: '0:20',
          heroEmoji: '🏆', titleLines: ['Nous avons', 'un gagnant !'], titleSize: 52,
          doLabel: '🎬 Reel du gagnant — à filmer', doTag: { label: 'vidéo', kind: 'vid' }, isVideo: true,
          caption: `🎬 À FILMER : le moment du tirage (Appee tire le gagnant à l'écran) + la tête du gagnant si tu peux.\nC'est LE contenu le plus partagé, toujours. Court et sincère.`,
          why: 'Annoncer le gagnant en vidéo prouve que le concours est vrai — et donne envie de jouer au prochain.',
        },
        {
          id: 'post-merci', postWhen: { day: 'Le lendemain', note: 'J+1 après le tirage' }, format: 'post', bg: 'warm', mode: 'thanks', badges: ['ig', 'fb'],
          heroEmoji: '💛', titleLines: ['Merci à', 'tou·te·s !'], titleSize: 62, subtitle: 'On recommence très vite.', pill: 'Bravo à notre gagnant·e 🎉', pillGhost: true,
          doLabel: 'Post de remerciement', doTag: { label: 'légende', kind: 'txt' },
          caption: `MERCI à tou·te·s d'avoir joué 🙏 Bravo à notre gagnant·e 🎉 — et pour les autres : on remet ça très vite, restez dans le coin 👀`,
          why: 'Remercier fidélise ceux qui n\'ont pas gagné : bien traités, ils reviendront et rejoueront la prochaine fois.',
        },
      ],
    },
  ];
}

const lower = s => s ? s.charAt(0).toLowerCase() + s.slice(1) : s;
const cap = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
// Raccourcit sans jamais couper au milieu d'un mot
function shortWords(s, max) {
  s = (s || '').trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const sp = cut.lastIndexOf(' ');
  return (sp > max * 0.5 ? cut.slice(0, sp) : cut).trim() + '…';
}
function splitTitle(s, max = 16) {
  const words = (s || '').split(' ');
  const lines = []; let cur = '';
  words.forEach(w => {
    if ((cur + ' ' + w).trim().length > max && cur) { lines.push(cur.trim()); cur = w; }
    else cur = (cur + ' ' + w).trim();
  });
  if (cur) lines.push(cur);
  return lines.slice(0, 3);
}
